import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { AuthLoading } from "@/components/auth-loading";
import { FAQ_ITEMS, LandingFaq } from "@/components/landing-faq";
import { PublicLayout } from "@/components/public-layout";
import { TaskCreationForm } from "@/components/task-creation-form";
import {
	buildBreadcrumb,
	buildFAQ,
	buildSoftwareApplication,
	buildWebPage,
	jsonLdScript,
} from "@/lib/seo/json-ld";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo/meta";
import { useAuthStore } from "@/stores/auth-store";
import { useOnboardingStore } from "@/stores/onboarding-store";

// Lazy-load authenticated layout — keeps sidebar, onboarding, etc. out of the landing page bundle
const AuthenticatedHome = lazy(() => import("@/components/authenticated-home"));

export const Route = createFileRoute("/")({
	head: () => {
		const pageTitle = "AI teammates that actually do your work.";
		const pageDescription = "Answers questions. Gives advice. Goes off and does tasks for you.";
		const canonicalUrl = getCanonical("/");

		const meta = buildMetaTags({
			title: pageTitle,
			description: pageDescription,
			canonical: canonicalUrl,
			ogType: "website",
		});

		const links = buildLinks({
			canonical: canonicalUrl,
		});

		const schemas = [
			buildWebPage({ name: pageTitle, description: pageDescription, url: canonicalUrl }),
			buildFAQ(FAQ_ITEMS),
			buildSoftwareApplication({ ratingValue: 4.8, ratingCount: 50, price: "0" }),
			buildBreadcrumb([{ name: "Home", url: canonicalUrl }]),
		];

		return {
			meta,
			links,
			scripts: schemas.map(jsonLdScript),
		};
	},

	component: HomePage,
});

function HomePage() {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const isInitialized = useAuthStore((state) => state.isInitialized);
	const { completed, openOnboarding } = useOnboardingStore();
	const [hasMounted, setHasMounted] = useState(false);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	// Show onboarding on first login (if not completed and authenticated)
	useEffect(() => {
		if (!hasMounted || !isInitialized || completed || !isAuthenticated) {
			return;
		}

		const timer = setTimeout(() => {
			openOnboarding();
		}, 500);
		return () => clearTimeout(timer);
	}, [completed, hasMounted, isAuthenticated, isInitialized, openOnboarding]);

	// Authenticated users: show sidebar layout (only after mount + auth confirmation)
	// hasMounted prevents hydration mismatch — SSR always renders the public layout below
	if (hasMounted && isInitialized && isAuthenticated) {
		return (
			<Suspense fallback={<AuthLoading />}>
				<AuthenticatedHome />
			</Suspense>
		);
	}

	// Default: public landing page
	// SSR renders this path → real content in the initial HTML (not a spinner)
	return (
		<PublicLayout>
			<TaskCreationForm />
			<LandingFaq />
		</PublicLayout>
	);
}
